import React from 'react';
import { View } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IColumnConfig, ISortModel } from '@/types/admin/dataTable';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useDesktopDataTableStyles } from './DesktopDataTableStyles';

type HeaderColumnConfig<T> = Pick<IColumnConfig<T>, 'key' | 'label' | 'sortable'>;

interface DesktopTableHeaderProps<T> {
  columns: HeaderColumnConfig<T>[];
  columnFlexGrow: number[];
  sortModel: ISortModel[];
  onSortChange?: (sortModel: ISortModel[]) => void;
  enableRowSelection?: boolean;
  selectedCount: number;
  totalCount: number;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
}

export const DesktopTableHeader = <T,>({
  columns,
  columnFlexGrow,
  sortModel,
  onSortChange,
  enableRowSelection,
  selectedCount,
  totalCount,
  onSelectAll,
  onDeselectAll,
}: DesktopTableHeaderProps<T>) => {
  const styles = useDesktopDataTableStyles();
  const { admin, redesign } = useAdminColors();

  return (
    <View style={styles.tableHeader}>
      <View style={styles.headerRow}>
        {enableRowSelection && (
          <View style={[styles.headerCell, { width: 50 }]}>
            <Hoverable
              hover="row"
              onPress={() => {
                if (selectedCount === totalCount) {
                  onDeselectAll?.();
                } else {
                  onSelectAll?.();
                }
              }}
              style={styles.checkbox}
            >
              {selectedCount === totalCount && totalCount > 0 && (
                <SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color={admin.formInput.borderFocused} />
              )}
            </Hoverable>
          </View>
        )}
        {columns.map((column, index) => (
          <Hoverable
            key={column.key as string}
            hover="row"
            style={[styles.headerCell, { flexGrow: columnFlexGrow[index], flexBasis: 0 }]}
            onPress={() => {
              if (column.sortable && onSortChange) {
                const currentSort = sortModel.find(sort => sort.field === column.key);
                const newDirection = currentSort?.sort === 'asc' ? 'desc' : 'asc';
                onSortChange([{ field: column.key as string, sort: newDirection }]);
              }
            }}
            disabled={!column.sortable}
          >
            <Text style={styles.headerText}>
              {column.label}
            </Text>
            {column.sortable && (
              <View style={styles.sortIndicator}>
                {sortModel.find(sort => sort.field === column.key) ? (
                  <Text style={[styles.sortIndicatorText, { color: admin.formInput.borderFocused }]}>
                    {sortModel.find(sort => sort.field === column.key)?.sort === 'asc' ? '↑' : '↓'}
                  </Text>
                ) : (
                  <Text style={[styles.sortIndicatorText, { color: redesign.text.dim }]}>↑</Text>
                )}
              </View>
            )}
          </Hoverable>
        ))}
      </View>
    </View>
  );
};
